/**
 * How many documents of one catalogue group a client may buy at once.
 *
 * PF v2 folds a Salesforce WorkType group into ONE shell whose variants are the
 * individual documents, and the drawer's variant picker is a radio — so a client
 * could buy one municipal document, not three. Legacy never worked that way: it
 * rendered a section per product type with a check-card per document
 * (`PurchaseFlow/common/ProductListing`), plain multi-select, and layered two
 * exclusivity rules on top.
 *
 * This module is the v2 home for those rules. It is pure — no store, no React —
 * so the drawer, the commit path and the tests all read the same predicate.
 *
 * ─── Rule 1: Bauakte (construction file) ───────────────────────────────────
 * Legacy: `productType === CONSTRUCTION_FILE` → selecting one unselects the
 * other (`ProductListing.tsx:188`). In v2 that whole set IS one WorkType group
 * (`CONSTRUCTION_FILE`, 3 `BAUAKTE_*` products on dev), so the rule collapses
 * into "this group stays single-select" — and the commit path additionally
 * replaces an existing line from the group, so re-opening the card cannot
 * smuggle a second one in.
 *
 * ─── Rule 2: Bewilligungsurkunde ───────────────────────────────────────────
 * Legacy displaces same-`documentSelectionGroup` products on selection
 * (`productIdsDisplacedBySelectionGroup`), because every product of the group
 * resolves to the SAME DocEstate document. The group is backend-computed from
 * `product.docestateSubclasses` and now rides the v2 catalogue too, so
 * `variantsDisplacedBySelectionGroup` below ports the rule as-is.
 *
 * Displacement is a courtesy, not the guarantee: placement rejects an order
 * carrying two of a group whatever the caller does.
 */

import type { ResolvedShellProduct, ResolvedVariant, SalesforceId } from 'models/purchaseFlowRevamp'

/** Catalogue-tab slug for authorities documents (`V2_CATEGORY_TO_SLUG`). */
export const DOCUMENTS_CATEGORY_SLUG = 'authorities_documents'

/**
 * Groups whose documents remain mutually exclusive. Keyed by `productGroupCode`
 * — the Salesforce WorkType name — so a group renamed in Salesforce stops
 * matching and degrades to multi-select rather than silently locking a
 * different group.
 */
export const SINGLE_SELECTION_DOCUMENT_GROUPS: ReadonlySet<string> = new Set([
  'CONSTRUCTION_FILE',
])

/** Whether this shell holds authorities-document products. */
/**
 * A package is excluded whatever its category. A pack of authorities documents
 * is filed under the documents category but is ONE product with a fixed
 * composition — treating it as a document group gave it a "Select a document"
 * picker, no price, and no contents list, because the catalogue opens a
 * document shell with nothing selected so the client can choose.
 */
export const isDocumentShell = (
  shell: Pick<ResolvedShellProduct, 'categoryId' | 'isBundle'> | null | undefined
): boolean => shell?.categoryId === DOCUMENTS_CATEGORY_SLUG && !shell?.isBundle

/**
 * Whether the client may tick several of this shell's variants at once. False
 * for every non-document shell (one photo package per line, unchanged) and for
 * the single-selection document groups above.
 */
export const allowsMultipleDocuments = (
  shell: Pick<ResolvedShellProduct, 'categoryId' | 'productGroupCode'> | null | undefined
): boolean =>
  isDocumentShell(shell) && !SINGLE_SELECTION_DOCUMENT_GROUPS.has(shell?.productGroupCode ?? '')

/**
 * Whether picking one variant of this shell must replace an existing cart line
 * from the same shell. True exactly for the single-selection document groups —
 * the multi-select ones legitimately produce one line per document.
 */
export const replacesExistingLine = (
  shell: Pick<ResolvedShellProduct, 'categoryId' | 'productGroupCode'> | null | undefined
): boolean => isDocumentShell(shell) && !allowsMultipleDocuments(shell)

/**
 * Ids of already-ticked variants that picking `variantId` displaces, because
 * they resolve to the same underlying DocEstate document.
 *
 * Ports legacy's `productIdsDisplacedBySelectionGroup`, including its one
 * subtlety: it runs **on selection only**, so unticking a document never
 * removes its sibling.
 *
 * Returns nothing for a variant with no group.
 */
export const variantsDisplacedBySelectionGroup = (
  variantId: SalesforceId,
  variants: readonly ResolvedVariant[],
  selectedVariantIds: readonly SalesforceId[]
): SalesforceId[] => {
  const group = variants.find((variant) => variant.id === variantId)?.documentSelectionGroup
  if (!group) return []

  return selectedVariantIds.filter((candidateId) => {
    if (candidateId === variantId) return false
    return variants.find((variant) => variant.id === candidateId)?.documentSelectionGroup === group
  })
}
